import { useId, useState, type FormEvent } from "react";
import { useCreateUser } from "../../../api/queries";
import type { UserRole } from "../../../api/types";
import { Button } from "../../../components/Button";
import { Modal } from "../../../components/Modal";
import { useToast } from "../../../toast/ToastContext";
import "../../../components/Form.css";

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
}

const ROLE_OPTIONS: { value: UserRole; label: string; hint: string }[] = [
  { value: "admin", label: "Admin", hint: "Full control, including user and audit-log administration." },
  { value: "operator", label: "Operator", hint: "Day-to-day management — no user admin, no host-escape ops." },
  { value: "viewer", label: "Viewer", hint: "Read-only access." },
  {
    value: "billing_admin",
    label: "Billing admin",
    hint: "Billing only — pay, change the plan, see the node count. No access to containers, secrets, or hosts.",
  },
];

export function CreateUserModal({ open, onClose }: CreateUserModalProps) {
  const usernameId = useId();
  const displayNameId = useId();
  const roleId = useId();
  const passwordId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateUser();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<UserRole>("operator");
  const [password, setPassword] = useState("");

  function reset() {
    setUsername("");
    setDisplayName("");
    setRole("operator");
    setPassword("");
  }

  function handleClose() {
    reset();
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedUsername = username.trim();
    mutation.mutate(
      { username: submittedUsername, displayName: displayName.trim(), role, password },
      {
        onSuccess: (created) => {
          pushToast({ title: `User "${created.username}" created.`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't create "${submittedUsername}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // Handed off to the mutation above; no reason to keep it in state a
    // moment longer, success or not (same reasoning as CreateSecretModal).
    setPassword("");
  }

  const canSubmit = username.trim() && displayName.trim() && password.length >= 8;

  return (
    <Modal open={open} title="New user" onClose={handleClose} initialFocusId={usernameId}>
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={usernameId}>
          <span>Username</span>
          <input
            id={usernameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            maxLength={64}
          />
          <span className="form-field__hint">What they sign in with. Letters, digits, '.', '_', '-' only.</span>
        </label>

        <label className="form-field" htmlFor={displayNameId}>
          <span>Display name</span>
          <input
            id={displayNameId}
            type="text"
            autoComplete="off"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            maxLength={128}
          />
        </label>

        <label className="form-field" htmlFor={roleId}>
          <span>Role</span>
          <select id={roleId} value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="form-field__hint">{ROLE_OPTIONS.find((o) => o.value === role)?.hint}</span>
        </label>

        <label className="form-field" htmlFor={passwordId}>
          <span>Password</span>
          <input
            id={passwordId}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
          <span className="form-field__hint">
            At least 8 characters. Write-only — nobody, including admins, can read it back later.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !canSubmit}>
            {mutation.isPending ? "Creating…" : "Create user"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
